import { Analytics } from '@boost/widget-integration-tae/lib';
import { Injectable } from 'framework/di';
import { pipe } from 'framework/functional';

import { ShopifyMarketHelper } from '../../helpers/shopify-market.helper';
import type { Product } from '../../providers/boost/models';
import { BoostAPI } from '../app/app.api';
import { AppService } from '../app/app.service';
import { B2BService } from '../b2b/b2b.service';
import { PlatformLoader } from '../platform/platform.loader';
import type { VolumeBundleData } from './volume-bundle.model';

interface BundleQueryParams extends Record<string, string | number | boolean | undefined> {
  product_id: number;
  shop: string;
  country?: string;
  customer_id?: string | number;
  locale?: string;
}

interface VolumeBundleResponse {
  bundles: VolumeBundleData[];
  meta?: {
    rid?: string;
    money_format?: string;
    money_format_with_currency?: string;
    currency?: string;
  };
}

@Injectable
export class VolumeBundleAPI extends BoostAPI {
  constructor(
    protected platformLoader: PlatformLoader,
    protected appService: AppService,
    protected b2bService: B2BService,
    protected shopifyMarketHelper: ShopifyMarketHelper
  ) {
    super(platformLoader);
    this.config.baseURL = this.appService.TAEAppConfig.bundleUrl || '';
  }

  protected buildBundleQueryParams(params: BundleQueryParams): BundleQueryParams {
    return pipe(
      { ...params },
      (p) => this.addCurrencyParams(p),
      (p) => this.b2bService.addB2BRequestParams(p),
      (p) => this.addLocalizationParams(p)
    );
  }

  async getVolumeBundles(productId: number): Promise<VolumeBundleData[]> {
    if (!productId) return [];

    const params: BundleQueryParams = {
      product_id: productId,
      shop: this.appService.TAEAppConfig.shop?.domain || '',
      country: this.platformLoader.platform.localization?.country || '',
      customer_id: this.platformLoader.platform.customer?.id,
    };

    const enhancedParams = this.buildBundleQueryParams(params);
    const filteredParams: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(enhancedParams)) {
      if (value !== undefined) {
        filteredParams[key] = value;
      }
    }
    const queryParams = this.buildDefaultQueryParams(filteredParams);

    try {
      const response = await this.get<VolumeBundleResponse>(`?${queryParams}`);
      if (response.status === 200 && response.data) {
        if (response.data.meta) {
          this.shopifyMarketHelper.restrictMainCurrency({
            money_format: response.data.meta.money_format || '',
            money_format_with_currency: response.data.meta.money_format_with_currency || '',
            currency: response.data.meta.currency || '',
          });

          this.appService
            .getTAEModule<Analytics>('Analytics')
            ?.saveRequestId(
              'product_page_volume_bundle' as 'product_page_bundle',
              response.data.meta.rid || '',
              response.data?.bundles
            );
        }
        return response.data.bundles || [];
      }
      return [];
    } catch {
      return [];
    }
  }

  async getProductById(productId: number): Promise<Product | null> {
    if (!productId) return null;

    const params: BundleQueryParams = {
      product_id: productId,
      shop: this.appService.TAEAppConfig.shop?.domain || '',
      country: this.platformLoader.platform.localization?.country || '',
      customer_id: this.platformLoader.platform.customer?.id,
    };

    const enhancedParams = this.buildBundleQueryParams(params);
    const filteredParams: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(enhancedParams)) {
      if (value !== undefined) {
        filteredParams[key] = value;
      }
    }
    const queryParams = this.buildDefaultQueryParams(filteredParams);

    const productUrl = this.appService.TAEAppConfig.productUrl || '';

    try {
      const response = await fetch(`${productUrl}?${queryParams}`);
      if (response.status === 200) {
        return (await response.json()) as Product;
      }
      return null;
    } catch {
      return null;
    }
  }
}
